<template>
  <section class="mx-auto max-w-6xl px-6 py-10">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs uppercase tracking-[0.4em] text-steel-400">Admin</p>
        <h1 class="mt-2 font-display text-3xl font-semibold text-white">Member Control Room</h1>
        <p class="mt-2 max-w-2xl text-sm text-steel-300">
          Manage roles, ban access, or delete member data. Changes apply immediately.
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="loading"
          @click="refreshUsers"
        >
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="mt-8 grid gap-4 sm:grid-cols-3">
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Total Members</p>
        <p class="mt-3 text-2xl font-semibold text-white">{{ stats.total }}</p>
      </div>
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Admins</p>
        <p class="mt-3 text-2xl font-semibold text-white">{{ stats.admins }}</p>
      </div>
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Banned</p>
        <p class="mt-3 text-2xl font-semibold text-white">{{ stats.banned }}</p>
      </div>
    </div>

    <div class="mt-12">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-steel-400">Reports</p>
          <h2 class="mt-2 font-display text-2xl font-semibold text-white">Moderation Queue</h2>
          <p class="mt-2 text-sm text-steel-300">
            Reports submitted by members for posts that may violate community rules.
          </p>
        </div>
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="reportsLoading"
          @click="fetchReports"
        >
          {{ reportsLoading ? 'Refreshing...' : 'Refresh reports' }}
        </button>
      </div>

      <p v-if="reportsError" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ reportsError }}
      </p>

      <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
        <div v-if="reportsLoading" class="px-6 py-6 text-sm text-steel-400">
          Loading reports...
        </div>
        <div v-else-if="!reports.length" class="px-6 py-6 text-sm text-steel-400">
          No reports yet.
        </div>
        <div v-else class="divide-y divide-white/5">
          <div v-for="report in reports" :key="report.id" class="px-6 py-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex flex-1 flex-col gap-3">
                <div class="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-400">
                  <span>{{ report.createdLabel }}</span>
                  <span class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
                    :class="report.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'">
                    {{ report.status || 'pending' }}
                  </span>
                </div>
                <p class="text-sm text-white">{{ report.reason }}</p>
                <div class="flex flex-wrap items-center gap-4 text-xs text-steel-300">
                  <router-link
                    v-if="report.postId"
                    :to="`/post/${report.postId}`"
                    class="text-neon-400 hover:text-neon-300"
                  >
                    View post
                  </router-link>
                  <router-link
                    v-if="report.reporterHandle"
                    :to="`/u/${report.reporterHandle}`"
                    class="text-steel-300 hover:text-white"
                  >
                    Reporter: @{{ report.reporterHandle }}
                  </router-link>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
                  @click="resolveReport(report)"
                >
                  Mark resolved
                </button>
                <button
                  v-if="report.postId"
                  type="button"
                  class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:bg-rose-500/20"
                  @click="openDeleteReportConfirm(report)"
                >
                  Delete post
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-12">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-steel-400">Site Issues</p>
          <h2 class="mt-2 font-display text-2xl font-semibold text-white">User Reports</h2>
          <p class="mt-2 text-sm text-steel-300">
            Issues submitted by members about the site experience.
          </p>
        </div>
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="siteReportsLoading"
          @click="fetchSiteReports"
        >
          {{ siteReportsLoading ? 'Refreshing...' : 'Refresh issues' }}
        </button>
      </div>

      <p v-if="siteReportsError" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ siteReportsError }}
      </p>

      <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
        <div v-if="siteReportsLoading" class="px-6 py-6 text-sm text-steel-400">
          Loading site issues...
        </div>
        <div v-else-if="!siteReports.length" class="px-6 py-6 text-sm text-steel-400">
          No issues reported yet.
        </div>
        <div v-else class="divide-y divide-white/5">
          <div v-for="report in siteReports" :key="report.id" class="px-6 py-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div class="flex flex-1 flex-col gap-3">
                <div class="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-400">
                  <span>{{ report.createdLabel }}</span>
                  <span class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
                    :class="report.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'">
                    {{ report.status || 'open' }}
                  </span>
                  <span class="rounded-full bg-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                    {{ report.category }}
                  </span>
                </div>
                <p class="text-sm text-white">{{ report.description }}</p>
                <div class="flex flex-wrap items-center gap-4 text-xs text-steel-300">
                  <span v-if="report.reporterHandle">Reporter: @{{ report.reporterHandle }}</span>
                  <a
                    v-if="report.pageUrl"
                    :href="report.pageUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-neon-400 hover:text-neon-300"
                  >
                    View page
                  </a>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
                  @click="resolveSiteReport(report)"
                >
                  Mark resolved
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteReportConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeDeleteReportConfirm"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete reported post?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will permanently remove the post. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeDeleteReportConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            :disabled="reportsLoading"
            @click="confirmDeleteReportedPost"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-col gap-4 rounded-3xl border border-white/10 bg-midnight-900/60 p-5 sm:flex-row sm:items-center">
      <div class="flex-1">
        <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
          Search
          <input
            v-model.trim="searchQuery"
            type="text"
            placeholder="Search handle or name"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @input="queueFetch"
          />
        </label>
      </div>
      <div class="sm:w-48">
        <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
          Status
          <select
            v-model="statusFilter"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @change="refreshUsers"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </select>
        </label>
      </div>
    </div>

    <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>
    <p v-if="successMessage" class="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
      {{ successMessage }}
    </p>

    <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
      <div class="hidden grid-cols-[2fr_1fr_1fr_2fr] gap-4 border-b border-white/10 px-6 py-4 text-xs uppercase tracking-[0.3em] text-steel-400 sm:grid">
        <span>User</span>
        <span>Role</span>
        <span>Status</span>
        <span>Actions</span>
      </div>
      <div v-if="loading" class="px-6 py-6 text-sm text-steel-400">
        Loading members...
      </div>
      <div v-else-if="!users.length" class="px-6 py-6 text-sm text-steel-400">
        No members found.
      </div>
      <div v-else>
        <div
          v-for="user in users"
          :key="user.id"
          class="grid gap-4 border-b border-white/5 px-6 py-5 sm:grid-cols-[2fr_1fr_1fr_2fr]"
        >
          <router-link
            :to="`/u/${user.handle}`"
            class="flex items-center gap-4 hover:opacity-80 transition"
          >
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/80">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div>
              <p class="text-sm font-semibold text-white">
                {{ user.fullName || 'DRVN Member' }}
                <span v-if="user.id === currentUserId" class="ml-2 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-steel-300">You</span>
              </p>
              <p class="text-xs text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>

          <div>
            <select
              :value="user.role"
              class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 text-xs uppercase tracking-[0.2em] text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              :disabled="user.id === currentUserId"
              @change="(event) => updateRole(user, event.target.value)"
            >
              <option v-for="role in roleOptions" :key="role" :value="role">{{ role }}</option>
            </select>
          </div>

          <div class="flex items-center gap-2">
            <span
              class="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
              :class="user.status === 'banned' ? 'bg-rose-500/20 text-rose-200' : 'bg-emerald-500/20 text-emerald-200'"
            >
              {{ user.status }}
            </span>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
              :disabled="user.id === currentUserId"
              @click="toggleBan(user)"
            >
              {{ user.status === 'banned' ? 'Unban' : 'Ban' }}
            </button>
            <button
              type="button"
              class="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-100 transition hover:bg-amber-500/20"
              :disabled="user.id === currentUserId"
              @click="deleteUserData(user)"
            >
              Delete Data
            </button>
            <button
              type="button"
              class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:bg-rose-500/20"
              :disabled="user.id === currentUserId"
              @click="deleteAuthUser(user)"
            >
              Delete Auth
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
      <p class="text-xs uppercase tracking-[0.3em] text-steel-500">
        Showing {{ users.length }} members
      </p>
      <button
        v-if="canLoadMore"
        type="button"
        class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
        :disabled="loadingMore"
        @click="loadMore"
      >
        {{ loadingMore ? 'Loading...' : 'Load more' }}
      </button>
    </div>

  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      users: [],
      loading: false,
      loadingMore: false,
      canLoadMore: true,
      searchQuery: '',
      statusFilter: 'all',
      errorMessage: '',
      successMessage: '',
      page: 0,
      pageSize: 25,
      searchTimer: null,
      currentUserId: '',
      roleOptions: ['admin', 'moderator', 'member'],
      reports: [],
      reportsLoading: false,
      reportsError: '',
      siteReports: [],
      siteReportsLoading: false,
      siteReportsError: '',
      showDeleteReportConfirm: false,
      reportToDelete: null,
      stats: {
        total: 0,
        admins: 0,
        banned: 0,
      },
    }
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.currentUserId = data?.session?.user?.id || ''
    await this.refreshUsers()
    await this.fetchReports()
    await this.fetchSiteReports()
  },
  methods: {
    queueFetch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.refreshUsers()
      }, 250)
    },
    async refreshUsers() {
      this.page = 0
      this.canLoadMore = true
      await this.fetchUsers(true)
    },
    async loadMore() {
      if (!this.canLoadMore || this.loadingMore) return
      this.page += 1
      await this.fetchUsers(false)
    },
    async fetchUsers(reset) {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = reset
      this.loadingMore = !reset
      const from = this.page * this.pageSize
      const to = from + this.pageSize - 1

      let query = supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path, role, status, created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to)

      if (this.statusFilter !== 'all') {
        query = query.eq('status', this.statusFilter)
      }

      const cleaned = this.searchQuery.replace(/^@/, '')
      if (cleaned) {
        query = query.or(`handle.ilike.%${cleaned}%,full_name.ilike.%${cleaned}%`)
      }

      const { data, error, count } = await query

      if (error) {
        this.errorMessage = error.message || 'Unable to load members.'
        this.loading = false
        this.loadingMore = false
        return
      }

      const normalized = (data || []).map((user) => {
        const avatar = getAvatarUrl({ avatarPath: user.avatar_path, avatarUrl: user.avatar_url })
        const fullName = user.full_name || ''
        const initials = (fullName || user.handle || 'D')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0].toUpperCase())
          .join('')
        return {
          id: user.id,
          handle: user.handle || 'member',
          fullName,
          avatar,
          role: user.role || 'member',
          status: user.status || 'active',
          initials,
        }
      })

      if (reset) {
        this.users = normalized
      } else {
        this.users = [...this.users, ...normalized]
      }

      this.canLoadMore = normalized.length === this.pageSize
      this.loading = false
      this.loadingMore = false

      this.stats.total = count || this.users.length
      this.stats.admins = this.users.filter((user) => user.role === 'admin').length
      this.stats.banned = this.users.filter((user) => user.status === 'banned').length
    },
    async updateRole(user, newRole) {
      if (user.role === newRole) return
      this.errorMessage = ''
      this.successMessage = ''
      const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', user.id)
      if (error) {
        this.errorMessage = error.message || 'Unable to update role.'
        return
      }
      user.role = newRole
      this.successMessage = `Role updated for @${user.handle}.`
      this.stats.admins = this.users.filter((item) => item.role === 'admin').length
    },
    async toggleBan(user) {
      if (user.id === this.currentUserId) return
      const makeBanned = user.status !== 'banned'
      const confirmText = makeBanned
        ? `Ban @${user.handle}? They will be signed out on next session refresh.`
        : `Unban @${user.handle} and restore access?`
      if (!window.confirm(confirmText)) return

      this.errorMessage = ''
      this.successMessage = ''
      const payload = makeBanned
        ? { status: 'banned', banned_at: new Date().toISOString() }
        : { status: 'active', banned_at: null }

      const { error } = await supabase.from('profiles').update(payload).eq('id', user.id)
      if (error) {
        this.errorMessage = error.message || 'Unable to update status.'
        return
      }
      user.status = payload.status
      this.successMessage = makeBanned ? `@${user.handle} banned.` : `@${user.handle} unbanned.`
      this.stats.banned = this.users.filter((item) => item.status === 'banned').length
    },
    async deleteUserData(user) {
      if (user.id === this.currentUserId) return
      if (!window.confirm(`Delete profile data for @${user.handle}? This cannot be undone.`)) return

      this.errorMessage = ''
      this.successMessage = ''
      const userId = user.id

      const { data: posts, error: postsError } = await supabase.from('posts').select('id').eq('user_id', userId)
      if (postsError) {
        this.errorMessage = postsError.message || 'Unable to load user posts.'
        return
      }
      const postIds = (posts || []).map((post) => post.id)
      if (postIds.length) {
        const { error: mediaError } = await supabase.from('post_media').delete().in('post_id', postIds)
        if (mediaError) {
          this.errorMessage = mediaError.message || 'Unable to delete post media.'
          return
        }
      }

      const deleteSteps = [
        supabase.from('post_comment_likes').delete().eq('user_id', userId),
        supabase.from('post_comments').delete().eq('user_id', userId),
        supabase.from('post_likes').delete().eq('user_id', userId),
        supabase.from('notifications').delete().or(`recipient_id.eq.${userId},actor_id.eq.${userId}`),
        supabase.from('posts').delete().eq('user_id', userId),
        supabase.from('profiles').delete().eq('id', userId),
      ]

      for (const step of deleteSteps) {
        const { error } = await step
        if (error) {
          this.errorMessage = error.message || 'Unable to delete user data.'
          return
        }
      }

      this.users = this.users.filter((item) => item.id !== userId)
      this.successMessage = `Deleted profile data for @${user.handle}.`
      this.stats.total = Math.max(0, this.stats.total - 1)
      this.stats.admins = this.users.filter((item) => item.role === 'admin').length
      this.stats.banned = this.users.filter((item) => item.status === 'banned').length
    },
    async deleteAuthUser(user) {
      if (user.id === this.currentUserId) return
      if (!window.confirm(`Delete auth user for @${user.handle}? This removes their login credentials.`)) return

      this.errorMessage = ''
      this.successMessage = ''
      const { error } = await supabase.functions.invoke('admin-delete-user', {
        body: { userId: user.id },
      })
      if (error) {
        this.errorMessage = error.message || 'Unable to delete auth user.'
        return
      }
      this.successMessage = `Auth user deleted for @${user.handle}.`
    },
    async fetchReports() {
      this.reportsError = ''
      this.reportsLoading = true

      const { data, error } = await supabase
        .from('post_reports')
        .select('id, post_id, reporter_id, reason, status, created_at')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        this.reportsError = error.message || 'Unable to load reports.'
        this.reportsLoading = false
        return
      }

      const reporterIds = (data || []).map((report) => report.reporter_id).filter(Boolean)
      let profilesById = new Map()

      if (reporterIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, handle, full_name, avatar_url, avatar_path')
          .in('id', reporterIds)

        profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]))
      }

      this.reports = (data || []).map((report) => {
        const reporter = profilesById.get(report.reporter_id) || {}
        const reporterAvatar = getAvatarUrl({
          avatarPath: reporter.avatar_path,
          avatarUrl: reporter.avatar_url,
        })
        return {
          id: report.id,
          postId: report.post_id,
          reporterId: report.reporter_id,
          reporterHandle: reporter.handle || '',
          reporterName: reporter.full_name || '',
          reporterAvatar,
          reason: report.reason || '',
          status: report.status || 'pending',
          createdLabel: report.created_at ? new Date(report.created_at).toLocaleString() : '',
        }
      })

      this.reportsLoading = false
    },
    async fetchSiteReports() {
      this.siteReportsError = ''
      this.siteReportsLoading = true

      const { data, error } = await supabase
        .from('site_reports')
        .select('id, user_id, category, description, page_url, status, created_at, profiles (handle, full_name)')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        this.siteReportsError = error.message || 'Unable to load site reports.'
        this.siteReportsLoading = false
        return
      }

      this.siteReports = (data || []).map((report) => ({
        id: report.id,
        userId: report.user_id,
        category: report.category || 'other',
        description: report.description || '',
        pageUrl: report.page_url || '',
        status: report.status || 'open',
        createdLabel: report.created_at ? new Date(report.created_at).toLocaleString() : '',
        reporterHandle: report.profiles?.handle || '',
        reporterName: report.profiles?.full_name || '',
      }))

      this.siteReportsLoading = false
    },
    async resolveSiteReport(report) {
      if (!report?.id) return
      const { error } = await supabase.from('site_reports').update({ status: 'resolved' }).eq('id', report.id)
      if (error) {
        this.siteReportsError = error.message || 'Unable to update site report.'
        return
      }
      report.status = 'resolved'
    },
    async resolveReport(report) {
      if (!report?.id) return
      const { error } = await supabase.from('post_reports').update({ status: 'resolved' }).eq('id', report.id)
      if (error) {
        this.reportsError = error.message || 'Unable to update report.'
        return
      }
      report.status = 'resolved'
    },
    openDeleteReportConfirm(report) {
      if (!report?.postId) return
      this.reportToDelete = report
      this.showDeleteReportConfirm = true
    },
    closeDeleteReportConfirm() {
      this.showDeleteReportConfirm = false
      this.reportToDelete = null
    },
    async confirmDeleteReportedPost() {
      if (!this.reportToDelete?.postId) {
        this.closeDeleteReportConfirm()
        return
      }

      const { error } = await supabase.from('posts').delete().eq('id', this.reportToDelete.postId)
      if (error) {
        this.reportsError = error.message || 'Unable to delete post.'
        this.closeDeleteReportConfirm()
        return
      }

      this.reportToDelete.status = 'resolved'
      this.successMessage = 'Post deleted.'
      this.closeDeleteReportConfirm()
    },
  },
}
</script>
